import React, { useState, useEffect, useRef } from 'react';
import './Header.scss';
import { BREAKPOINTS } from '../../constants/breakpoints';
import Cart from '../UI/Icons/Cart';
import CTAButton from '../UI/CTA/CTAButton/CTAButton';
import MobileMenu from './MobileMenu/MobileMenu';
import DrawerMenu from './DrawerMenu/DrawerMenu';
import DesktopNav from './DesktopNav/DesktopNav';
import HamburgerButton from './HamburgerButton/HamburgerButton';
import AnnouncementBar from './AnnouncementBar/AnnouncementBar';
import CartCount from '../Cart/CartCount/CartCount';
import { isEmpty } from '../../util/isEmpty';
const bodyScrollLock = require('body-scroll-lock');
const enableBodyScroll = bodyScrollLock.enableBodyScroll;

const DESKTOP_WIDTH = BREAKPOINTS.large;

/**
 * Renders the header
 * @param cartLink
 * @param logoLink
 * @param {Object} cta
 * @param {Object} login
 * @param {String} logo
 * @param {Array} menu_one
 * @param {Array} menu_two
 * @param {Array} menu_three
 * @param {String} menu_one_title
 * @param {String} menu_two_title
 * @param {String} menu_three_title
 * @param {Boolean} isHomepage
 * @param {Object} announcement_bar
 * @param {object} shoppingCart
 * @param {number} itemCount
 * @param {function} getCurrentCart
 * @param {boolean} disableCart
 * @returns {JSX.Element}
 * @constructor
 */
const Header = ({
  cartLink,
  logoLink,
  cta,
  login,
  logo,
  menu_one,
  menu_two,
  menu_three,
  menu_one_title,
  menu_two_title,
  menu_three_title,
  isHomepage = false,
  announcement_bar,
  shoppingCart,
  itemCount,
  getCurrentCart = null,
  disableCart = false,
}) => {
  let scrollAmount = 0;
  const [scrolledDown, setScrolledDown] = useState(false);
  const links = {};
  links[menu_one_title] = menu_one;
  links[menu_two_title] = menu_two;
  links[menu_three_title] = menu_three;
  const linkItems = Object.keys(links);

  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < DESKTOP_WIDTH);
  const [menuItems, setMenuItems] = useState(
    linkItems.map((link) => {
      return {
        links: links[link],
        isExpanded: false,
      };
    }),
  );
  const drawer = useRef();
  const announcementBarEl = useRef();
  const [headerHeight, setHeaderHeight] = useState(isMobile ? 48 : 96);
  const [navBgTransparent, setNavBgTransparent] = useState(
    !isMobile && window.scrollY < DESKTOP_WIDTH,
  );
  const [announcementBarHeight, setAnnouncementBarHeight] = useState(null);
  const [announcementBarScrolled, setAnnouncementBarScrolled] = useState(false);

  useEffect(() => {
    !disableCart && getCurrentCart();
    setIsMobile(window.innerWidth < DESKTOP_WIDTH);
    setNavBgTransparent(!isMobile && window.scrollY < DESKTOP_WIDTH);
    announcement_bar.show && setAnnouncementBarHeight(announcementBarEl.current.offsetHeight);
    window.addEventListener('scroll', scroll);
    window.addEventListener('touchmove', scroll);
    window.addEventListener('resize', handleWindowResize);

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('wheel', scroll);
    };
  }, []);

  const scroll = () => {
    if (scrollAmount < window.scrollY && window.scrollY > headerHeight) {
      setScrolledDown(true);
      scrollAmount = window.scrollY;
    } else {
      setScrolledDown(false);
      scrollAmount = window.scrollY;
    }

    announcement_bar.show && setAnnouncementBarScrolled(window.scrollY >= headerHeight);
    setNavBgTransparent(!isMobile && window.scrollY < headerHeight);
  };

  /**
   * Responsible for setting the state for isMobile based on device size
   */
  const handleWindowResize = () => {
    setIsMobile(window.innerWidth < DESKTOP_WIDTH);
    setNavBgTransparent(window.scrollY < headerHeight);
    setHeaderHeight(isMobile ? 48 : 96);
    announcement_bar.show && setAnnouncementBarHeight(announcementBarEl.current.offsetHeight);
  };

  /**
   * Sets the state for menuOpen which shows/hides menu
   * @param {number} i - index
   */
  const toggleMenu = (i) => {
    setMenuOpen(!menuOpen);
    toggleMenuItems(i);
  };

  /**
   * Toggles the children of the parent chosen menu
   * @param {number} chosenMenu - index of chosen menu
   */
  const toggleMenuItems = (chosenMenu) => {
    setMenuItems(
      linkItems.map((link, i) => {
        return {
          links: links[link],
          isExpanded: i === chosenMenu,
        };
      }),
    );
  };

  /**
   * Handles the toggling of the menu and body scroll lock
   */
  const handleToggle = () => {
    toggleMenu();
    enableBodyScroll(drawer);
  };

  /**
   * Render links shown at the end of the desktop menu next to cart
   * @returns {JSX.Element}
   */
  const trailingLinks = () => {
    return (
      <div className="c-header__trailing-links">
        <ul className="c-header__trailing-list">
          <li className="c-header__trainling-list-item">
            <a className="c-header__login-link cta color-black" href={login.url}>
              {login.text}
            </a>
          </li>
          <li className="c-header__trailing-list-item">
            <CTAButton
              type={'secondary'}
              link={cta.url}
              text={cta.text}
              target={'_self'}
              additionalClasses={'color-black cta'}
            />
          </li>
        </ul>
      </div>
    );
  };

  /**
   * Render main header navigation elements based on device size. Hamburger menu for mobile, main nav for desktop
   * @returns {JSX.Element}
   */
  const renderHeaderBasedOnSize = () =>
    isMobile ? (
      <HamburgerButton drawer={drawer} toggleMenu={toggleMenu} />
    ) : (
      <DesktopNav
        classes="cta color-black"
        onClickAction={toggleMenu}
        drawer={drawer}
        linkItems={linkItems}
        menuItems={menuItems}
      />
    );

  /**
   * Render full menu based on device size
   * @returns {JSX.Element}
   */
  const renderNavBasedOnSize = () =>
    isMobile ? (
      <MobileMenu
        cta={cta}
        links={links}
        linkItems={linkItems}
        login={login}
        menuOpen={menuOpen}
        handleToggle={handleToggle}
      />
    ) : (
      <DrawerMenu
        menuItems={menuItems}
        linkItems={linkItems}
        drawer={drawer}
        handleToggle={handleToggle}
        menuOpen={menuOpen}
        toggleMenuItems={toggleMenuItems}
      />
    );

  /**
   * Render trailing part of the menu containing CTA links based on device size
   * @returns {null|JSX.Element}
   */
  const renderTrailingBasedOnSize = () => (isMobile ? null : trailingLinks());

  const { cart } = shoppingCart?.get || {};
  const cartCount = isEmpty(cart) ? itemCount : cart.item_count;

  return (
    <header
      className={`c-header background-color-gray ${scrolledDown ? 'is-hidden' : ''} ${
        isHomepage && !isMobile && navBgTransparent ? 'is-transparent' : ''
      } ${isHomepage ? '' : 'border-bottom'}`}
    >
      {announcement_bar.show && (
        <AnnouncementBar
          domRef={announcementBarEl}
          text={announcement_bar?.text}
          url={announcement_bar?.url}
          classes="body-3 color-white background-color-blue"
          styles={
            announcementBarScrolled
              ? { marginTop: `-${announcementBarHeight}px` }
              : { marginTop: '0px' }
          }
        />
      )}

      <nav>
        <div className="c-header__menu">{renderHeaderBasedOnSize()}</div>
        <div className="c-header__logo">
          <a className="c-header__logo-link" href={logoLink}>
            <img src={logo} alt="Logo" className="c-header__logo-img" />
          </a>
        </div>
        <div className="c-header__trailing">
          {renderTrailingBasedOnSize()}
          <a href={cartLink}>
            <div className="c-header__cart-icon-container">
              {cartCount > 0 && (
                <div className="c-header__cart-count color-white body-3">
                  <CartCount count={cartCount} cart={cart} />
                </div>
              )}
              <Cart />
            </div>
          </a>
        </div>
        {renderNavBasedOnSize()}
      </nav>
    </header>
  );
};

export default Header;
